/**
 * When a member's travel preferences count as complete. Shared so the web account page and the
 * mobile Account screen flag the same thing: completing these measurably improves Elara's
 * recommendations.
 */

import type { ProfilePreference } from '@elara/domain/models/profile-preferences';
import type { PreferenceCategory } from '@elara/domain/models/schemas/shared-schemas';
import type { TravelerProfile } from '@elara/domain/models/schemas/TravelerProfileSchema';

/** The cabin-class fields on `travelerProfile`. Each needs at least one choice. */
export const REQUIRED_CABIN_FIELDS = ['domesticFlightClass', 'internationalFlightClass'] as const;

/**
 * Preference categories that need at least one entry. Dining, dietary and other preferences (and
 * travel credit cards, which are not a category) are optional: they still appear on the checklist.
 */
export const REQUIRED_PREFERENCE_CATEGORIES: readonly PreferenceCategory[] = [
  'airport',
  'hotel',
  'key_date',
  'destination',
];

/** One category's saved preferences, in profile order; entries saved before categories existed count as general. */
export function preferencesIn(
  tp: Partial<TravelerProfile> | undefined,
  category: PreferenceCategory,
): ProfilePreference[] {
  return (tp?.preferences ?? []).filter((entry) => ('category' in entry ? entry.category : 'general') === category);
}

/** True once every cabin-class field and every required preference category has at least one entry. */
export function travelPreferencesComplete(tp: Partial<TravelerProfile> | undefined): boolean {
  return (
    REQUIRED_CABIN_FIELDS.every((key) => (tp?.[key]?.length ?? 0) > 0) &&
    REQUIRED_PREFERENCE_CATEGORIES.every((category) => preferencesIn(tp, category).length > 0)
  );
}
